import { FC, useEffect, useRef, useState } from 'react'
import { KTSVG } from '../../../../../../_metronic/helpers'
import { Formik, Form, FormikValues } from 'formik'
import * as Yup from 'yup'

import {
  FormikTextInput,
  FormikTextField,
  FormikDateSelector,
} from '../../../../../components/widgets'

import { StatusErrorMessage } from '../../../../../components/widgets'
import { DOCHourRatingDropdown, generateDLMDOC, initDLMWebDOCModelValues } from '../../../../core/doclabel'
import { getErrorMessage, downloadFile, array_fill_keys } from '../../../../../components/helpers'
import { IDLMWebDOCModel } from '../../../../core/doclabel'

import { SubmitButtonRow } from '../../../../../components/layout'
import { pickBy } from 'lodash'
                                                                                                                                                                                                                                                                                                                                                                                                                                                              

const dlmGenerateDOCSchema: any = Yup.object({
  docId: Yup.number().integer().label('DOC Id'),
  docAttachmentId: Yup.number().integer().label('DOC Attachment ID'),
  siteApplicationId: Yup.number().integer().label('Site Application ID'),
  inspectionId: Yup.number().integer().label('Inspection ID'),
  cbRef: Yup.string().trim().label('CB Reference'),
  companyName: Yup.string().trim().label('Company Name').required(),
  installer: Yup.string().trim().label('Installer Company'),
  projectName: Yup.string().trim().label('Project Name').required(),
  otherRef: Yup.string().trim().label('Other Reference'),
  productType: Yup.string().trim().label('Product Type').required(),
  cocNo: Yup.string().trim().label('COC Number'),
  brand: Yup.string().trim().label('Brand').required(),
  model: Yup.string().trim().label('Model').required(),
  areaOfCoverage: Yup.number().label('Area of Coverage'),
  unit: Yup.string().trim().label('Unit of Measure').required(),
  hourRating: Yup.string().trim().matches(/^(?!0$)(?!0\.0$)(-|\d+(\.\d{1})?)$/, "Select from options.").required().label('Hour Rating'),
  location: Yup.string().trim().label('Location').required(),
  hasIssueDate: Yup.boolean(),
  issueDate: Yup.string().trim()
  .when('hasIssueDate', {
    is: true,
    then: (schema) => schema.required('Set Issue Date'),
  }),
})

type Props = {
  baseDOC: IDLMWebDOCModel
  onSuccess?: Function
  doClose: Function
  hasIssueDate?: boolean
}


const GenerateDOCModal: FC<Props> = ({baseDOC, onSuccess, doClose, hasIssueDate = false}) => {

  const formikRef = useRef<any>(null);
  const [initValues, SetInitValues] = useState<IDLMWebDOCModel>({...initDLMWebDOCModelValues});

  const submitDOCModel = async(values: any, actions: FormikValues) => {

    const { setStatus, setSubmitting } = actions;

    // filter empty values, except areaOfCoverage (always include);
    const payload = pickBy(values, (value, key) => ((value || ['areaOfCoverage'].includes(key)) && (! ['hasIssueDate'].includes(key))));

    setStatus('');
    setSubmitting(true);

    await generateDLMDOC(payload)
    .then( (res: any) => {
      if (res.status !== 200) {
        setStatus('Something happened');
        return;
      }
      const filename = res.headers['content-disposition'].split('filename=')[1].replace(/"/gm,'');
      downloadFile(res.data, filename);
      if (onSuccess) {
        onSuccess(values);
      }
      doClose();
    })
    .catch((err: any) => {
      setStatus(getErrorMessage(err));
    })
    .finally(() => {
      setSubmitting(false);
    })
  }


  useEffect(() => {
    if (! (baseDOC)) return;
    SetInitValues({
      ...initDLMWebDOCModelValues,
      ...baseDOC,
      hasIssueDate
    });
    setTimeout(function () {
      if (formikRef.current) formikRef.current.validateForm();
    }, 300)

  }, [baseDOC, hasIssueDate]);


  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  return (
    <>
    <div className='modal fade show d-block' id='kt_modal_generate_doc' role='dialog' tabIndex={-1} aria-modal='true' >
      <div className='modal-dialog modal-dialog-centered mw-550px'>
        <div className='modal-content' style={{backgroundColor: '#f0e0be'}}>
          <div className='modal-header'>
            <div>
              <h2 className='fw-bolder'>
                Generate DOC
              </h2>
              <div className="d-flex flex-row flex-wrap align-items-start justify-content-between gap-6 p-2">
                <div className="d-flex flex-row flex-center gap-2">
                  <label className='fw-bold text-muted fs-7'>COC No: </label>
                  <div className="className='fw-bolder fs-7 text-dark">{initValues.cocNo}</div>
                </div>
              </div>
            </div>
            <div
              className='btn btn-icon btn-sm btn-active-icon-danger'
              data-kt-producttypes-modal-action='close'
              style={{cursor: 'pointer'}}
              onClick={() => doClose()}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
            </div>
          </div>
          <div className='modal-body scroll-y py-4 mb-7 min-h-80px'>
            <Formik
              innerRef={formikRef}
              initialValues={initValues}
              validationSchema={dlmGenerateDOCSchema}
              enableReinitialize={true}
              onSubmit={submitDOCModel}
              validateOnMount={true}
              initialTouched={{
                ...array_fill_keys([ 'companyName', 'projectName', 'planNo', 'productType', 'brand', 'model', 'unit', 'hourRating', 'location' ], true ),
                issueDate: hasIssueDate
              }}
            >
            {({status}) => (
              <Form id='kt_doclabel_submitresponse_form' className='form' noValidate>
                <div
                  className='d-flex flex-column scroll-y me-n7 ps-2 pe-7'
                  id='kt_generated_doc_modal_scroll'
                  data-kt-scroll='true'
                  data-kt-scroll-activate='{default: false, lg: true}'
                  data-kt-scroll-max-height='auto'
                  data-kt-scroll-dependencies='#kt_modal_generate_doc'
                  data-kt-scroll-wrappers='#kt_generated_doc_modal_scroll'
                  data-kt-scroll-offset='300px'
                >
                  <div className="row mb-4 gy-2">
                    <FormikTextInput size="12 col-md-6" label="CB Reference" name="cbRef" required={true} />
                    {hasIssueDate &&
                    <FormikDateSelector size="12 col-md-6" label="Set Issue Date" name="issueDate" required={true} />
                    }
                  </div>

                  <div className="row mb-2">
                    <FormikTextInput size="12 col-md-6" label="Company Name" name="companyName" required={true} />
                    <FormikTextInput size="12 col-md-6" label="Brand" name="brand" required={true} />
                  </div>
                  <div className="row mb-2">
                    <FormikTextInput size="12 col-md-6" label="Model" name="model" required={true} />
                    <FormikTextInput size="12 col-md-6" label="Product Type" name="productType" required={true} />
                  </div>
                  <div className="row mb-2">
                    <FormikTextInput size="12 col-md-6" label="Installer Company" name="installer" />
                  </div>
                  <div className="row mb-2">
                    <FormikTextField size="12" label="Project Name" name="projectName" required={true} rows={1} />
                    <FormikTextField size="12" label="Location" name="location" required={true} rows={1} />
                  </div>

                  <div className="row mb-2">
                    <FormikTextInput size="12 col-md-6" label="SCDF Building Plan No" name="planNo" />
                    <FormikTextInput size="12 col-md-6" label="Other Reference" name="otherRef" />
                  </div>
                  <div className="row mb-2">
                    <FormikTextInput size="6 col-md-3" label="Area of Coverage" name="areaOfCoverage" readOnly />
                    <FormikTextInput size="6 col-md-3" label="Unit" name="unit" required={true} />
                    <DOCHourRatingDropdown size="6 col-md-3" label="Hour Rating" name="hourRating" />           
                  </div>
                </div>
                <StatusErrorMessage status={status} />
                <SubmitButtonRow doClose={doClose} submitText="Generate DOC" />
              </Form>
            )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
    <div className='modal-backdrop fade show'></div>
    </>
    )
}

export {GenerateDOCModal}
